/*
 * Pandadrum-style behaviours: hero slideshow and the "1 or 2 drums" offer picker.
 * Loaded as a deferred classic script (not a module) so it can never run before the theme's import map.
 */
(() => {
  if (window.pdKitLoaded) return;
  window.pdKitLoaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Texts and storefront URLs in the visitor's language, printed by snippets/pd-head.liquid.
  // Shopify's `t` filter HTML-escapes texts (n'est -> n&#39;est): decode them before using them as plain text.
  const decodeText = (value) => {
    const area = document.createElement('textarea');
    area.innerHTML = value || '';
    return area.value;
  };
  const rawStrings = window.pdStrings || {};
  const strings = Object.keys(rawStrings).reduce((all, key) => {
    all[key] = decodeText(rawStrings[key]);
    return all;
  }, {});
  function route(name, path) {
    const routes = window.pdRoutes || {};
    if (routes[name]) return routes[name];
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }
  const canHover = window.matchMedia('(hover: hover)');
  const OFFER = '[data-pd-offer]';
  const OFFER_RADIO = '.pd-offer__row[role="radio"]';

  /* ---------- Slideshow: .pd-slides > .pd-slide, with .pd-slides__dot and .pd-slides__arrow--prev/--next ---------- */
  function initSlides(root) {
    if (root.pdReady) return;
    root.pdReady = true;

    const scope = root.closest('.shopify-section') || root.parentElement || root;
    const slides = Array.from(root.querySelectorAll('.pd-slide'));
    const dots = Array.from(scope.querySelectorAll('.pd-slides__dot'));
    if (slides.length < 2) return;

    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer = null;
    let stopped = false; // set for good after the first manual arrow, dot or swipe
    let hovering = false;
    let focused = false;

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.setAttribute('aria-hidden', String(i !== index));
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', String(i === index));
      });
    };

    const restart = () => {
      window.clearInterval(timer);
      timer = null;
      if (stopped || hovering || focused) return;
      const seconds = Number(root.dataset.autoplay) || 0;
      if (seconds > 0 && !reduceMotion.matches) timer = window.setInterval(() => show(index + 1), seconds * 1000);
    };

    scope.addEventListener('click', (event) => {
      const dot = event.target.closest('.pd-slides__dot');
      if (dot) show(Number(dot.dataset.index) || 0);
      else if (event.target.closest('.pd-slides__arrow--prev')) show(index - 1);
      else if (event.target.closest('.pd-slides__arrow--next')) show(index + 1);
      else return;
      stopped = true;
      restart();
    });

    // Pause while the pointer is over the slideshow or focus is inside it; resume when both leave.
    root.addEventListener('mouseenter', () => {
      if (!canHover.matches) return;
      hovering = true;
      restart();
    });
    root.addEventListener('mouseleave', () => {
      if (!hovering) return;
      hovering = false;
      restart();
    });
    root.addEventListener('focusin', () => {
      focused = true;
      restart();
    });
    root.addEventListener('focusout', (event) => {
      if (event.relatedTarget && root.contains(event.relatedTarget)) return;
      focused = false;
      restart();
    });

    let startX = null;
    root.addEventListener('touchstart', (event) => { startX = event.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const distance = event.changedTouches[0].clientX - startX;
      startX = null;
      if (Math.abs(distance) < 40) return;
      show(index + (distance < 0 ? 1 : -1));
      stopped = true;
      restart();
    });

    show(index);
    restart();
  }

  /* ---------- Offer picker (blocks/pd-offer.liquid) ----------
     Colours are identified by their option value id (swatch data-value) and variant id (data-variant-id),
     never by their name: names are translated on /fr, /es... and are only displayed (swatch data-name). */
  function readOffer(root) {
    return {
      mode: root.dataset.mode || 'single',
      color1: root.dataset.color1 || '',
      color2: root.dataset.color2 || '',
    };
  }

  // Selects the colour in the theme's own (hidden) variant picker, so price, photos and the form follow.
  // Matched on the option value id (data-option-value-id), the same in every language.
  function pickThemeColor(root, valueId) {
    const scope = root.closest('.shopify-section') || document;
    const picker = scope.querySelector(`variant-picker[data-product-id="${root.dataset.productId}"]`);
    if (!picker || !valueId) return;
    const input = Array.from(picker.querySelectorAll('input[type="radio"]')).find((el) => el.dataset.optionValueId === valueId);
    if (input) {
      if (!input.checked) input.click();
      return;
    }
    const option = Array.from(picker.querySelectorAll('select option')).find((el) => el.dataset.optionValueId === valueId);
    const select = option && option.closest('select');
    if (select && !option.selected) {
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function swatchOf(root, valueId) {
    return Array.from(root.querySelectorAll('.pd-swatch')).find((el) => el.dataset.value === valueId) || null;
  }

  function sectionOf(root) {
    return root.closest('.shopify-section') || document;
  }

  // "plus-navy.webp?v=123" from a Shopify image URL: the part that names the picture, without the size.
  function mediaKey(url) {
    return (url || '').split('&')[0].split('/').pop();
  }

  function swatchMedia(root, value) {
    const swatch = swatchOf(root, value);
    return swatch ? swatch.dataset.pdMedia || '' : '';
  }

  // Shows the picture of `value` in place of the selected variant's picture in the theme's gallery (main slide,
  // thumbnail and zoom), without changing the selected variant. The theme rebuilds the gallery on every variant
  // change, which brings back its own pictures. Changing src on the same <img> keeps the old picture on screen
  // until the new one is ready, so the big image never goes blank.
  function showInGallery(root, value) {
    const gallery = sectionOf(root).querySelector('media-gallery');
    if (!gallery) return;
    if (!gallery.pdBaseKey) {
      gallery.pdBaseKey = mediaKey(gallery.querySelector('.product-media__image, img')?.getAttribute('src'));
    }
    const base = gallery.pdBaseKey;
    const from = gallery.pdShownKey || base;
    const to = mediaKey(swatchMedia(root, value)) || base;
    if (!base || !from || from === to) return;
    gallery.querySelectorAll('img, source').forEach((el) => {
      ['srcset', 'src'].forEach((attr) => {
        const current = el.getAttribute(attr);
        if (current && current.includes(from)) el.setAttribute(attr, current.split(from).join(to));
      });
    });
    gallery.pdShownKey = to;
    const slideshow = gallery.slideshow || gallery.querySelector('slideshow-component');
    if (to !== base && slideshow && slideshow.current !== 0 && typeof slideshow.select === 'function') {
      slideshow.select(0);
    }
  }

  // Loads (and decodes) every colour's big picture at the size the gallery uses, the first time the shopper
  // points at or focuses the offer block, so changing colour shows the new picture at once.
  function preloadVariantImages(root) {
    if (root.pdPreloaded) return;
    root.pdPreloaded = true;
    const reference = sectionOf(root).querySelector('media-gallery .product-media__image, media-gallery img');
    const sizes = reference?.getAttribute('sizes') || '100vw';
    const widths = (reference?.getAttribute('srcset') || '')
      .split(',')
      .map((part) => parseInt(part.trim().split(/\s+/)[1], 10))
      .filter(Boolean);
    const urls = new Set(Array.from(root.querySelectorAll('.pd-swatch[data-pd-media]')).map((el) => el.dataset.pdMedia));
    root.pdPreloads = [];
    urls.forEach((url) => {
      const img = new Image();
      img.decoding = 'async';
      if (widths.length) {
        img.sizes = sizes;
        img.srcset = widths.map((w) => `${url.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`).join(', ');
      }
      img.src = url;
      if (img.decode) img.decode().catch(() => {});
      root.pdPreloads.push(img);
    });
  }

  // The theme's sticky add to cart bar only shows the product name and the button (assets/pd-sticky.css). While the
  // 2-drum offer is selected, .pd-sticky--duo adds a small "x2" chip after the name; its button clicks the main add
  // to cart button, whose submit is caught below, so both drums go in the cart. The theme morphs the bar on every
  // variant change, so a MutationObserver re-applies the class if it is lost.
  function stickyOf(root) {
    return sectionOf(root).querySelector(`sticky-add-to-cart[data-product-id="${root.dataset.productId}"]`);
  }

  function syncSticky(root) {
    const sticky = stickyOf(root);
    if (!sticky) return;
    const duo = readOffer(root).mode === 'duo' && Boolean(root.dataset.duoTotal);
    if (sticky.classList.contains('pd-sticky--duo') !== duo) sticky.classList.toggle('pd-sticky--duo', duo);
  }

  function watchSticky(root) {
    const sticky = stickyOf(root);
    if (!sticky || sticky.pdWatched) return;
    sticky.pdWatched = true;
    new MutationObserver(() => syncSticky(root)).observe(sticky, { childList: true, subtree: true, attributeFilter: ['class'] });
  }

  function setMode(root, mode) {
    if (root.dataset.mode === mode) return;
    root.dataset.mode = mode;
    if (mode !== 'duo') showInGallery(root, readOffer(root).color1);
    paintOffer(root);
  }

  function paintOffer(root) {
    const { mode, color1, color2 } = readOffer(root);
    root.querySelectorAll('.pd-offer__card').forEach((card) => {
      const on = card.dataset.offer === mode;
      card.classList.toggle('is-selected', on);
      // The radio is the card's top row, so the colour swatches below it stay real, operable buttons.
      const radio = card.querySelector(OFFER_RADIO);
      if (!radio) return;
      radio.setAttribute('aria-checked', String(on));
      radio.tabIndex = on ? 0 : -1;
    });
    root.querySelectorAll('[data-pd-show]').forEach((el) => {
      el.hidden = el.dataset.pdShow !== mode;
    });
    root.querySelectorAll('.pd-swatches').forEach((group) => {
      const value = group.dataset.slot === '2' ? color2 : color1;
      group.querySelectorAll('.pd-swatch').forEach((swatch) => {
        const on = swatch.dataset.value === value;
        swatch.classList.toggle('is-selected', on);
        swatch.setAttribute('aria-pressed', String(on));
      });
    });
    root.querySelectorAll('[data-pd-color-name]').forEach((el) => {
      const swatch = swatchOf(root, el.dataset.pdColorName === '2' ? color2 : color1);
      if (swatch) el.textContent = swatch.dataset.name || '';
    });
    syncSticky(root);
  }

  function initOffer(root) {
    if (root.pdReady) return;
    root.pdReady = true;
    const cards = Array.from(root.querySelectorAll('.pd-offer__card'));

    root.addEventListener('click', (event) => {
      const swatch = event.target.closest('.pd-swatch');
      if (swatch) {
        if (swatch.getAttribute('aria-disabled') === 'true') return;
        const value = swatch.dataset.value;
        const card = swatch.closest('.pd-offer__card');
        if (card) root.dataset.mode = card.dataset.offer;
        preloadVariantImages(root);
        if (swatch.closest('.pd-swatches')?.dataset.slot === '2') {
          root.dataset.color2 = value;
          showInGallery(root, value);
        } else {
          root.dataset.color1 = value;
          showInGallery(root, value);
          pickThemeColor(root, value);
        }
        paintOffer(root);
        return;
      }
      const card = event.target.closest('.pd-offer__card');
      if (!card) return;
      setMode(root, card.dataset.offer);
    });

    const warmUp = () => preloadVariantImages(root);
    ['pointerover', 'focusin', 'touchstart'].forEach((type) => {
      root.addEventListener(type, warmUp, { once: true, passive: true });
    });
    watchSticky(root);

    root.addEventListener('keydown', (event) => {
      const radio = event.target.closest(OFFER_RADIO);
      if (!radio || event.target !== radio) return;
      const card = radio.closest('.pd-offer__card');
      if (!card) return;
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        setMode(root, card.dataset.offer);
        return;
      }
      const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
      if (!step) return;
      event.preventDefault();
      const next = cards[(cards.indexOf(card) + step + cards.length) % cards.length];
      setMode(root, next.dataset.offer);
      next.querySelector(OFFER_RADIO)?.focus();
    });

    paintOffer(root);
  }

  function showOfferError(root, message) {
    const box = root.querySelector('.pd-offer__error');
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
    window.setTimeout(() => { box.hidden = true; }, 8000);
  }

  // Adds both drums at once; ignores repeat submits (double clicks) while a request is still running.
  async function addDuo(root, component) {
    if (root.pdBusy) return;
    root.pdBusy = true;
    const button = component.querySelector('button[type="submit"], [name="add"]');
    const wasDisabled = button ? button.disabled : false;
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }
    try {
      await sendDuo(root, component);
    } finally {
      root.pdBusy = false;
      if (button) {
        button.removeAttribute('aria-busy');
        button.disabled = wasDisabled;
      }
    }
  }

  // Adds both drums at once, then lets the theme open and refresh its cart drawer like a normal add to cart.
  async function sendDuo(root, component) {
    // [{ value: option value id, id: variant id, available }], one entry per colour (blocks/pd-offer.liquid).
    const variants = JSON.parse(root.querySelector('script[data-pd-variants]')?.textContent || '[]');
    const { color1, color2 } = readOffer(root);
    const first = variants.find((variant) => String(variant.value) === color1);
    const second = variants.find((variant) => String(variant.value) === color2);
    if (!first || !second || !first.id || !second.id || !first.available || !second.available) {
      showOfferError(root, root.dataset.unavailableText || strings.offerUnavailable || '');
      return;
    }
    const items = first.id === second.id
      ? [{ id: first.id, quantity: 2 }]
      : [{ id: first.id, quantity: 1 }, { id: second.id, quantity: 1 }];

    // Locale-aware URLs (/fr/cart...), so the cart drawer sections come back in the visitor's language.
    const cartUrl = route('cart', 'cart');
    const cartAddUrl = route('cartAdd', 'cart/add.js');

    let Lines = null;
    try {
      ({ CartLinesUpdateEvent: Lines } = await import('@shopify/events'));
    } catch (error) {
      Lines = null;
    }
    const deferred = Lines && Lines.createPromise ? Lines.createPromise() : null;
    if (deferred) {
      component.dispatchEvent(new Lines({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      }));
    }

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const detail = {
        items: cart.items,
        source: 'pd-offer',
        sourceId: 'pd-offer',
        itemCount: 2,
        productId: component.dataset.productId,
        sections: data.sections,
        didError: Boolean(data.status),
      };
      if (deferred) deferred.resolve({ cart: Lines.createCartFromAjaxResponse(cart), detail });
      if (data.status) {
        showOfferError(root, data.description || data.message || strings.addToCartError || '');
      } else if (!deferred) {
        window.location.href = cartUrl;
      }
    } catch (error) {
      if (deferred) deferred.reject(error);
      window.location.href = cartUrl;
    }
  }

  // Runs before the theme's own submit handler (registered on document) when the duo offer is selected.
  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const component = form.closest('product-form-component');
    if (!component) return;
    const root = document.querySelector(`${OFFER}[data-product-id="${component.dataset.productId}"]`);
    if (!root || readOffer(root).mode !== 'duo') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addDuo(root, component);
  }, true);

  function initAll(scope) {
    scope.querySelectorAll('.pd-slides').forEach(initSlides);
    scope.querySelectorAll(OFFER).forEach(initOffer);
  }

  initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => initAll(event.target.closest('.shopify-section') || document));
})();
